/**
 * Analytics Service - Lightweight client-side analytics tracking
 *
 * Tracks session lifecycle, page views, quiz engagement, and daily active usage.
 * Events are buffered and sent in batches to the backend API.
 */

import { apiClient } from '@shared/api/apiClient';

// --- Types ---

interface AnalyticsEventPayload {
  eventType: string;
  sessionId: string;
  occurredAt: string;
  metadata: string | null;
}

interface TrackEventsResponse {
  eventsTracked: number;
}

// --- Constants ---

const STORAGE_KEYS = {
  SESSION_ID: 'analytics_session_id',
  SESSION_START: 'analytics_session_start',
  LAST_ACTIVE_DATE: 'analytics_last_active_date',
  QUESTIONS_THIS_SESSION: 'analytics_questions_session',
} as const;

const FLUSH_INTERVAL_MS = 30_000; // Flush events every 30 seconds
const MAX_BUFFER_SIZE = 50; // Flush when buffer reaches this size

// --- Module State ---

let eventBuffer: AnalyticsEventPayload[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let isFlushing = false;
let currentSessionId: string | null = null;

// --- Internal Helpers ---

/**
 * Generate a unique session identifier using crypto.randomUUID when available.
 */
function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now().toString(36)}-${Date.now().toString(36)}`;
}

/**
 * Get ISO timestamp for the current moment.
 */
function nowISO(): string {
  return new Date().toISOString();
}

/**
 * Get or create the current session ID.
 */
function getSessionId(): string {
  if (currentSessionId) return currentSessionId;

  const stored = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (stored) {
    currentSessionId = stored;
    return stored;
  }

  const newId = generateSessionId();
  currentSessionId = newId;
  localStorage.setItem(STORAGE_KEYS.SESSION_ID, newId);
  return newId;
}

/**
 * Send buffered events to the backend API.
 */
async function flushEvents(): Promise<void> {
  if (isFlushing || eventBuffer.length === 0) return;

  // Don't flush if not authenticated
  const token = localStorage.getItem('authToken');
  if (!token) {
    eventBuffer = [];
    return;
  }

  isFlushing = true;
  const eventsToSend = [...eventBuffer];
  eventBuffer = [];

  try {
    await apiClient.post<TrackEventsResponse>('/api/analytics/events', {
      events: eventsToSend,
    });
  } catch (error) {
    // Re-queue failed events (but cap to avoid memory issues)
    if (eventsToSend.length + eventBuffer.length <= MAX_BUFFER_SIZE * 2) {
      eventBuffer = [...eventsToSend, ...eventBuffer];
    }
    console.warn('Analytics: failed to flush events', error);
  } finally {
    isFlushing = false;
  }
}

/**
 * Add an event to the buffer and flush if needed.
 */
function bufferEvent(eventType: string, metadata?: Record<string, unknown>): void {
  const event: AnalyticsEventPayload = {
    eventType,
    sessionId: getSessionId(),
    occurredAt: nowISO(),
    metadata: metadata ? JSON.stringify(metadata) : null,
  };

  eventBuffer.push(event);

  // Flush immediately if buffer is large
  if (eventBuffer.length >= MAX_BUFFER_SIZE) {
    flushEvents().then();
  }
}

// --- Public API ---

/**
 * Analytics service for tracking user events.
 * Call `init()` when the app mounts and `destroy()` when it unmounts.
 */
export const analyticsService = {
  /**
   * Initialize analytics: start session, set up periodic flushing,
   * and register beforeunload handler.
   */
  init(): void {
    // Start periodic flush
    if (!flushTimer) {
      flushTimer = setInterval(flushEvents, FLUSH_INTERVAL_MS);
    }

    // Flush on page unload
    window.addEventListener('beforeunload', analyticsService.handleUnload);
    document.addEventListener('visibilitychange', analyticsService.handleVisibilityChange);
  },

  /**
   * Clean up analytics: flush remaining events, clear timers.
   */
  destroy(): void {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    window.removeEventListener('beforeunload', analyticsService.handleUnload);
    document.removeEventListener('visibilitychange', analyticsService.handleVisibilityChange);
    flushEvents().then();
  },

  /**
   * Track session start. Called when user logs in or app loads with auth.
   */
  trackSessionStart(): void {
    const sessionId = generateSessionId();
    currentSessionId = sessionId;
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
    localStorage.setItem(STORAGE_KEYS.SESSION_START, nowISO());
    localStorage.setItem(STORAGE_KEYS.QUESTIONS_THIS_SESSION, '0');

    bufferEvent('session_start');
    analyticsService.trackDailyActive();
  },

  /**
   * Track session end. Called on logout or page unload.
   */
  trackSessionEnd(): void {
    const sessionStart = localStorage.getItem(STORAGE_KEYS.SESSION_START);
    const questionsThisSession = Number.parseInt(
      localStorage.getItem(STORAGE_KEYS.QUESTIONS_THIS_SESSION) || '0',
      10
    );

    let durationSeconds = 0;
    if (sessionStart) {
      durationSeconds = Math.round((Date.now() - new Date(sessionStart).getTime()) / 1000);
    }

    bufferEvent('session_end', {
      durationSeconds,
      questionsAnswered: questionsThisSession,
    });

    // Flush immediately since we may be leaving the page
    flushEvents().then();
  },

  /**
   * Track a page view event.
   */
  trackPageView(path: string): void {
    bufferEvent('page_view', { path });
  },

  /**
   * Track when a question is answered during a quiz.
   */
  trackQuestionAnswered(data: {
    topicId: string;
    questionIndex: number;
    isCorrect: boolean;
    responseTimeSeconds: number;
  }): void {
    // Increment session question counter
    const current = Number.parseInt(
      localStorage.getItem(STORAGE_KEYS.QUESTIONS_THIS_SESSION) || '0',
      10
    );
    localStorage.setItem(STORAGE_KEYS.QUESTIONS_THIS_SESSION, String(current + 1));

    bufferEvent('question_answered', data);
  },

  /**
   * Track when a quiz is started.
   */
  trackQuizStarted(data: { topicId: string; attemptId: string }): void {
    bufferEvent('quiz_started', data);
  },

  /**
   * Track when a quiz is completed.
   */
  trackQuizCompleted(data: {
    topicId: string;
    attemptId: string;
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    timeSpentSeconds: number;
  }): void {
    bufferEvent('quiz_completed', data);
  },

  /**
   * Track daily active usage. Called once per calendar day per session.
   * Uses localStorage to avoid duplicate tracking within the same day.
   */
  trackDailyActive(): void {
    const today = new Date().toISOString().split('T')[0];
    const lastTrackedDate = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVE_DATE);

    if (lastTrackedDate !== today) {
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVE_DATE, today);
      bufferEvent('daily_active', { date: today });
    }
  },

  /**
   * Force flush all buffered events.
   */
  flush(): Promise<void> {
    return flushEvents();
  },

  /**
   * Handle page unload - send session_end and flush.
   */
  handleUnload(): void {
    analyticsService.trackSessionEnd();
  },

  /**
   * Handle visibility change - flush events when tab is hidden.
   */
  handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      flushEvents().then();
    }
  },

  /**
   * Reset analytics state (e.g., on logout).
   */
  reset(): void {
    eventBuffer = [];
    currentSessionId = null;
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.SESSION_START);
    localStorage.removeItem(STORAGE_KEYS.QUESTIONS_THIS_SESSION);
    // Don't remove LAST_ACTIVE_DATE - it persists across sessions
  },
};
